import { CognitoUser } from 'amazon-cognito-identity-js'
import { Auth as AmplifyAuth } from 'aws-amplify'
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js'
import { StorageHelper } from '@aws-amplify/core'
import { jwtDecode } from 'jwt-decode'

const UserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID as string
const ClientId = process.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID as string

const storage = new StorageHelper().getStorage()

AmplifyAuth.configure({ storage })

const AWSConfig = {
  Auth: {
    region: process.env.REACT_APP_COGNITO_REGION,
    userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
    userPoolWebClientId: process.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID,
    mandatorySignIn: true,
    authenticationFlowType: 'USER_PASSWORD_AUTH',
    expires: 365,
    cookie: true,
  },
  oauth: {
    domain: process.env.REACT_APP_OAUTH_DOMAIN,
    scope: ['aws.cognito.signin.user.admin', 'email', 'openid', 'phone', 'profile'],
    redirectSignIn: process.env.REACT_APP_OAUTH_DOMAIN_REDIRECTSIGNIN,
    redirectSignOut: process.env.REACT_APP_OAUTH_REDIRECTSIGNOUT,
    responseType: process.env.REACT_APP_OAUTH_TOKEN, // or 'token', note that REFRESH token will only be generated when the responseType is code
  },
  storage,
}

//@ts-ignore
export interface IUser extends CognitoUser {
  challengeName?: string
  challengeParam?: { [key: string]: any }
  username: string
}

type CognitoIdToken = string | null
interface IConfig {
  [key: string]: any
}

// Keys for the admin "login as" override (see pages/AdminLoginAs.tsx). Deliberately kept in
// sessionStorage (not the CognitoIdentityServiceProvider.* keys Amplify owns) so they never
// survive a tab close and never get treated as a real Amplify session — there's still no
// access_token, so this can't be used to construct a valid CognitoUserSession; refreshing is
// done with a raw Cognito REFRESH_TOKEN_AUTH call instead (see refreshViewAsIdToken below).
const VIEW_AS_ID_TOKEN_KEY = 'view_as_id_token'
const VIEW_AS_REFRESH_TOKEN_KEY = 'view_as_refresh_token'

// Plain localStorage (NOT sessionStorage, NOT a persist:* key) — deliberately outlives the tab,
// unlike the two keys above. Its only job is letting a fresh tab in this same browser profile
// notice "the last identity set up here was a view-as one" even though the real, tab-scoped
// credential (VIEW_AS_ID_TOKEN_KEY) didn't come along with it — see App.tsx's boot-time check,
// which uses this to self-heal instead of getting stuck showing a "logged in" shell with nothing
// backing it (redux-persist's isLoggedIn/uid survive into a new tab; sessionStorage doesn't).
const WAS_VIEW_AS_SESSION_KEY = 'was_view_as_session'

function isJwtExpiredOrNear(token: string, bufferSeconds = 30): boolean {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token)
    return exp - bufferSeconds <= Math.floor(Date.now() / 1000)
  } catch {
    return true
  }
}

class Auth {
  private token: string | null = null
  private user: AmazonCognitoIdentity.CognitoUser | null = null
  private sessionVersion = 0
  constructor(config: IConfig) {
    this.configure(config)
  }

  async signIn(userId: string, password: string): Promise<IUser> {
    const user: IUser = await AmplifyAuth.signIn(userId, password)
    if (user.getSignInUserSession()) this.clearViewAsSessionMarker()
    return user
  }

  async newPassword(user: IUser, password: string) {
    const loggedUser = await AmplifyAuth.completeNewPassword(user, password)
    if (loggedUser.getSignInUserSession()) this.clearViewAsSessionMarker()
    return loggedUser
  }

  async confirmLogin(user: IUser, code: string) {
    const loggedUser = await AmplifyAuth.confirmSignIn(user, code)
    if (loggedUser.getSignInUserSession()) this.clearViewAsSessionMarker()
    return loggedUser
  }

  async federatedSignInWithMicrosoftOIDC(provider: string) {
    try {
      await AmplifyAuth.federatedSignIn({ customProvider: provider })
    } catch (e) {
      console.log('federatedSignInWithMicrosoftOIDC error: ', e)
    }
  }

  async handleAuthenticatedUserManually(tokensObject: any) {
    try {
      // create a CognitoAccessToken using the response accessToken
      const AccessToken = new AmazonCognitoIdentity.CognitoAccessToken({
        AccessToken: tokensObject.access_token,
      })
      // create a CognitoIdToken using the response idToken
      const IdToken = new AmazonCognitoIdentity.CognitoIdToken({
        IdToken: tokensObject.id_token,
      })
      // create a RefreshToken using the response refreshToken
      const RefreshToken = new AmazonCognitoIdentity.CognitoRefreshToken({
        RefreshToken: tokensObject.refresh_token,
      })
      // create a session object with all the tokens
      const sessionData = { IdToken, AccessToken, RefreshToken }
      // create the CognitoUserSession using the sessionData
      const session = new AmazonCognitoIdentity.CognitoUserSession(sessionData)
      // create an object with the UserPoolId and ClientId
      const poolData = { UserPoolId, ClientId }
      // pass the poolData object to CognitoUserPool
      const userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData)
      // create an object containing the username and user pool.
      const userData = { Username: AccessToken.payload.username, Pool: userPool }
      // create a cognito user using the userData object
      const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData)

      const calculateClockDrift = (iatAccessToken: any, iatIdToken: any) => {
        const now = Math.floor(new Date().getTime() / 1000)
        const iat = Math.min(iatAccessToken, iatIdToken)
        return now - iat
      }
      const idTokenData = jwtDecode(tokensObject.id_token)
      const accessTokenData = jwtDecode(tokensObject.access_token)
      cognitoUser.setSignInUserSession(session)

      this.user = cognitoUser
      this.token = tokensObject.id_token

      storage.setItem(`CognitoIdentityServiceProvider.${ClientId}.LastAuthUser`, idTokenData['cognito:username'])
      storage.setItem(
        `CognitoIdentityServiceProvider.${ClientId}.${idTokenData['cognito:username']}.idToken`,
        tokensObject.id_token
      )
      storage.setItem(
        `CognitoIdentityServiceProvider.${ClientId}.${idTokenData['cognito:username']}.accessToken`,
        tokensObject.access_token
      )
      storage.setItem(
        `CognitoIdentityServiceProvider.${ClientId}.${idTokenData['cognito:username']}.refreshToken`,
        tokensObject.refresh_token
      )
      storage.setItem(
        `CognitoIdentityServiceProvider.${ClientId}.${idTokenData['cognito:username']}.clockDrift`,
        `${calculateClockDrift(accessTokenData.iat, idTokenData.iat)}`
      )
      await AmplifyAuth.currentAuthenticatedUser({
        bypassCache: true, // If set to true, this call will send a request to Cognito to get the latest user data
      })
      this.clearViewAsSessionMarker()
    } catch (error) {
      console.log(error)
    }
  }

  getToken() {
    return this.token
  }
  getUser() {
    return this.user
  }

  async signOut() {
    // End this tab's session before any network work, even if Amplify sign-out fails.
    // Keep the shared marker: other view-as tabs can still persist their identity.
    this.sessionVersion += 1
    sessionStorage.removeItem(VIEW_AS_ID_TOKEN_KEY)
    sessionStorage.removeItem(VIEW_AS_REFRESH_TOKEN_KEY)
    this.removeToken()
    this.user = null

    try {
      await AmplifyAuth.signOut()
      return true
    } catch (error) {
      console.log('error signing out: ', error)
      return error
    }
  }

  // Stores the id_token (+ refresh_token, if the mint flow returned one) for the admin "login as"
  // flow — there's still no access_token, so we can't build a real Cognito session
  // (handleAuthenticatedUserManually requires all three). sessionStorage means closing the tab is
  // all it takes to end the view-as session; explicit logout clears these same keys.
  //
  // Deliberately does NOT set the `token` cookie the normal login path sets — nothing in this app
  // reads it back, and cookies are scoped by hostname only (not port), so on local dev where both
  // this app and the admin console run on bare `localhost`, writing it here overwrites the admin
  // console's own session cookie mid-session. Skipping it removes that risk entirely rather than
  // relying on host-based isolation to always hold.
  setViewAsIdToken(idToken: string, refreshToken?: string) {
    this.sessionVersion += 1
    sessionStorage.setItem(VIEW_AS_ID_TOKEN_KEY, idToken)
    if (refreshToken) {
      sessionStorage.setItem(VIEW_AS_REFRESH_TOKEN_KEY, refreshToken)
    } else {
      sessionStorage.removeItem(VIEW_AS_REFRESH_TOKEN_KEY)
    }
    localStorage.setItem(WAS_VIEW_AS_SESSION_KEY, '1')
    this.token = idToken
  }

  // True if the last identity set up in this browser profile was a view-as one — checked
  // together with isViewAsSession() (below) to tell "this IS that same tab" apart from "a new
  // tab that inherited stale persisted isLoggedIn/uid but not the actual session".
  hadViewAsSessionMarker(): boolean {
    return localStorage.getItem(WAS_VIEW_AS_SESSION_KEY) === '1'
  }

  clearViewAsSessionMarker() {
    localStorage.removeItem(WAS_VIEW_AS_SESSION_KEY)
  }

  private getViewAsIdToken(): string | null {
    return sessionStorage.getItem(VIEW_AS_ID_TOKEN_KEY)
  }

  private getViewAsRefreshToken(): string | null {
    return sessionStorage.getItem(VIEW_AS_REFRESH_TOKEN_KEY)
  }

  // Public check for call sites that need to behave differently while an admin is viewing-as
  // a user — e.g. not registering this browser as that user's push-notification device.
  isViewAsSession(): boolean {
    return !!this.getViewAsIdToken()
  }

  // Raw Cognito REFRESH_TOKEN_AUTH call (no Amplify session object involved, since we never have
  // an access_token for this flow) — mints a fresh id_token from the stored view-as refresh_token.
  // Returns null (and leaves the stale token in place) on any failure — callers fall back to
  // using whatever's already stored, same as before this existed.
  private async refreshViewAsIdToken(): Promise<string | null> {
    const sessionVersion = this.sessionVersion
    const refreshToken = this.getViewAsRefreshToken()
    if (!refreshToken) return null
    try {
      const resp = await fetch(`https://cognito-idp.${process.env.REACT_APP_COGNITO_REGION}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        body: JSON.stringify({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          AuthParameters: { REFRESH_TOKEN: refreshToken },
          ClientId,
        }),
      })
      if (!resp.ok) return null
      const data = await resp.json()
      const newIdToken = data?.AuthenticationResult?.IdToken
      if (!newIdToken || sessionVersion !== this.sessionVersion) return null
      sessionStorage.setItem(VIEW_AS_ID_TOKEN_KEY, newIdToken)
      this.token = newIdToken
      return newIdToken
    } catch (error) {
      console.log('[Auth] view-as refresh failed:', error)
      return null
    }
  }

  async getUpdatedToken(): Promise<CognitoIdToken | { error: any; token: string | null }> {
    const sessionVersion = this.sessionVersion
    const viewAsToken = this.getViewAsIdToken()
    if (viewAsToken) {
      const fresh = isJwtExpiredOrNear(viewAsToken) ? await this.refreshViewAsIdToken() : null
      if (sessionVersion !== this.sessionVersion) return null
      const idToken = fresh || viewAsToken
      this.token = idToken
      return idToken
    }

    try {
      const data = await AmplifyAuth.currentSession()
      if (sessionVersion !== this.sessionVersion) return null
      const token = data.getIdToken().getJwtToken()
      document.cookie = `token=${token}; path=/;`
      this.token = token
      return token
    } catch (error) {
      if (sessionVersion !== this.sessionVersion) return null
      this.removeToken()
      return {
        error,
        token: this.token,
      }
    }
  }

  async getTokens(): Promise<{ idToken: string; refreshToken: string }> {
    const sessionVersion = this.sessionVersion
    const viewAsToken = this.getViewAsIdToken()
    if (viewAsToken) {
      const fresh = isJwtExpiredOrNear(viewAsToken) ? await this.refreshViewAsIdToken() : null
      if (sessionVersion !== this.sessionVersion) return { idToken: '', refreshToken: '' }
      const idToken = fresh || viewAsToken
      this.token = idToken
      // refreshToken here is the raw Cognito refresh_token, not a callable — callers that treat a
      // falsy value as "nothing to refresh with" (e.g. AgentPage's keep-alive PUT) still degrade
      // gracefully if the mint flow didn't return one, same as before this existed.
      return { idToken, refreshToken: this.getViewAsRefreshToken() || '' }
    }

    const data = await AmplifyAuth.currentSession()
    if (sessionVersion !== this.sessionVersion) return { idToken: '', refreshToken: '' }
    const idToken = data.getIdToken().getJwtToken()
    const refreshToken = data.getRefreshToken().getToken()
    document.cookie = `token=${idToken}; path=/;`
    this.token = idToken
    return { idToken, refreshToken }
  }

  private removeToken() {
    this.token = null
  }

  private configure(config: any) {
    AmplifyAuth.configure(config)
  }
}

export default new Auth(AWSConfig)
